/**
 * VITAL — Cart Drawer
 *
 * API surface:
 *   VitalCart.open()      — opens drawer, fetches cart
 *   VitalCart.close()     — closes drawer
 *   VitalCart.refresh()   — re-fetches cart without toggling
 *
 * Triggers:
 *   [data-open-cart]   — any element, opens drawer on click
 *   .vcd-overlay       — click to close
 *   Escape key         — closes drawer
 *
 * Cart mutations via:
 *   /cart/change.js    — qty update / remove (qty=0)
 *   /cart.js           — read cart state
 *
 * Free shipping threshold: configured via window.vcdFreeShippingThreshold
 * (set in Liquid before this script loads, defaults to 150000 COP)
 */
(function () {
  'use strict';

  var FREE_SHIP_THRESHOLD = window.vcdFreeShippingThreshold || 150000; // COP
  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── DOM references (populated on first open) ── */
  var overlay   = null;
  var drawer    = null;
  var bodyEl    = null;
  var countEl   = null;
  var subtotalEl = null;
  var shipFillEl = null;
  var shipTextEl = null;
  var checkoutBtn = null;

  var isOpen    = false;
  var isFetching = false;

  /* ═══════════════════════════════════════════
     HELPERS
  ════════════════════════════════════════════ */
  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatMoney(cents) {
    /* Shopify prices come in as cents (COP has no real sub-unit, but API uses smallest unit) */
    var amount = cents / 100;
    return '$\u202F' + amount.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function refs() {
    overlay    = document.getElementById('vcd-overlay');
    drawer     = document.getElementById('vcd');
    bodyEl     = document.getElementById('vcd-body');
    countEl    = document.getElementById('vcd-count');
    subtotalEl = document.getElementById('vcd-subtotal');
    shipFillEl = document.getElementById('vcd-ship-fill');
    shipTextEl = document.getElementById('vcd-ship-text');
    checkoutBtn = document.getElementById('vcd-checkout');
  }

  /* ═══════════════════════════════════════════
     OPEN / CLOSE
  ════════════════════════════════════════════ */
  function open() {
    if (!overlay) refs();
    if (!overlay || !drawer) return;

    overlay.classList.add('open');
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    isOpen = true;

    /* prevent body scroll */
    document.body.style.overflow = 'hidden';

    /* Focus management */
    var closeBtn = drawer.querySelector('.vcd__close');
    if (closeBtn) {
      setTimeout(function () { closeBtn.focus(); }, prefersReduced ? 0 : 380);
    }

    /* Trap focus */
    drawer.addEventListener('keydown', trapFocus);

    refresh();
  }

  function close() {
    if (!overlay) refs();
    if (!overlay || !drawer) return;

    overlay.classList.remove('open');
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    isOpen = false;

    document.body.style.overflow = '';
    drawer.removeEventListener('keydown', trapFocus);

    /* Return focus to last trigger */
    if (window._vcdLastTrigger) {
      try { window._vcdLastTrigger.focus(); } catch (e) {}
      window._vcdLastTrigger = null;
    }
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var focusable = drawer.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var first = focusable[0];
    var last  = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ═══════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  function renderSkeleton() {
    if (!bodyEl) return;
    bodyEl.innerHTML =
      '<div class="vcd__skeleton-item">' +
        '<div class="vcd__skel vcd__skel--thumb"></div>' +
        '<div style="flex:1;display:flex;flex-direction:column;gap:6px;padding-top:4px">' +
          '<div class="vcd__skel vcd__skel--line-a"></div>' +
          '<div class="vcd__skel vcd__skel--line-b"></div>' +
          '<div class="vcd__skel vcd__skel--line-c"></div>' +
        '</div>' +
      '</div>' +
      '<div class="vcd__skeleton-item">' +
        '<div class="vcd__skel vcd__skel--thumb"></div>' +
        '<div style="flex:1;display:flex;flex-direction:column;gap:6px;padding-top:4px">' +
          '<div class="vcd__skel vcd__skel--line-a"></div>' +
          '<div class="vcd__skel vcd__skel--line-b"></div>' +
          '<div class="vcd__skel vcd__skel--line-c"></div>' +
        '</div>' +
      '</div>';
  }

  function renderEmpty() {
    if (!bodyEl) return;
    bodyEl.innerHTML =
      '<div class="vcd__empty">' +
        '<div class="vcd__empty-icon">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true">' +
            '<path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>' +
            '<line x1="3" y1="6" x2="21" y2="6"/>' +
            '<path d="M16 10a4 4 0 01-8 0"/>' +
          '</svg>' +
        '</div>' +
        '<p class="vcd__empty-title">Tu carrito está vacío</p>' +
        '<p class="vcd__empty-sub">Descubre nuestros suplementos y añade tus favoritos</p>' +
        '<div class="vcd__empty-cta">' +
          '<a href="/collections" class="vital-btn-primary" style="display:inline-flex;align-items:center;gap:8px;text-decoration:none;padding:12px 24px;border-radius:12px;background:linear-gradient(135deg,#FFAA33,#E63991);color:#fff;font-family:Inter,system-ui,sans-serif;font-size:14px;font-weight:700;letter-spacing:.04em;">Ver productos</a>' +
        '</div>' +
      '</div>';

    /* Update footer */
    if (countEl) { countEl.textContent = '0'; countEl.style.display = 'none'; }
    if (subtotalEl) subtotalEl.textContent = formatMoney(0);
    if (checkoutBtn) checkoutBtn.setAttribute('disabled', '');
    updateShippingBar(0);
  }

  function renderCart(cart) {
    if (!bodyEl) return;

    /* Update header count */
    if (countEl) {
      countEl.textContent = cart.item_count;
      countEl.style.display = cart.item_count > 0 ? 'inline-flex' : 'none';
    }

    /* Update global cart badges */
    document.querySelectorAll('.vital-cart-badge, .vital-d-cart-badge, [data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });

    if (cart.item_count === 0) {
      renderEmpty();
      return;
    }

    /* Subtotal */
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    if (checkoutBtn) checkoutBtn.removeAttribute('disabled');
    updateShippingBar(cart.total_price);

    /* Items */
    var html = '';
    cart.items.forEach(function (item) {
      var imgHTML;
      if (item.featured_image && item.featured_image.url) {
        var src = item.featured_image.url.replace(/\?.*$/, '') + '?width=136';
        imgHTML = '<img src="' + esc(src) + '" alt="' + esc(item.product_title) + '" loading="lazy">';
      } else {
        imgHTML = '<span class="vcd__item-img-placeholder">💊</span>';
      }

      var priceHTML = '<span class="vcd__item-price">' + formatMoney(item.final_line_price) + '</span>';
      if (item.original_line_price > item.final_line_price) {
        priceHTML += '<span class="vcd__item-compare">' + formatMoney(item.original_line_price) + '</span>';
      }

      var variantHTML = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        variantHTML = '<span class="vcd__item-variant">' + esc(item.variant_title) + '</span>';
      }

      html +=
        '<div class="vcd__item" data-key="' + esc(item.key) + '">' +
          '<div class="vcd__item-img">' + imgHTML + '</div>' +
          '<div class="vcd__item-info">' +
            '<span class="vcd__item-name">' + esc(item.product_title) + '</span>' +
            variantHTML +
            '<div style="display:flex;align-items:center;gap:4px;margin-top:2px">' + priceHTML + '</div>' +
            '<div class="vcd__item-bottom">' +
              '<div class="vcd__stepper">' +
                '<button class="vcd__stepper-btn" data-action="decrease" data-key="' + esc(item.key) + '" data-qty="' + (item.quantity - 1) + '" aria-label="Reducir cantidad">' +
                  '<svg viewBox="0 0 12 12" aria-hidden="true"><line x1="2" y1="6" x2="10" y2="6"/></svg>' +
                '</button>' +
                '<span class="vcd__stepper-qty" aria-live="polite">' + item.quantity + '</span>' +
                '<button class="vcd__stepper-btn" data-action="increase" data-key="' + esc(item.key) + '" data-qty="' + (item.quantity + 1) + '" aria-label="Aumentar cantidad">' +
                  '<svg viewBox="0 0 12 12" aria-hidden="true"><line x1="6" y1="2" x2="6" y2="10"/><line x1="2" y1="6" x2="10" y2="6"/></svg>' +
                '</button>' +
              '</div>' +
              '<button class="vcd__remove" data-key="' + esc(item.key) + '" aria-label="Eliminar ' + esc(item.product_title) + '">' +
                '<svg viewBox="0 0 14 14" aria-hidden="true">' +
                  '<polyline points="3 3 11 11"/><polyline points="11 3 3 11"/>' +
                '</svg>' +
              '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
    });

    bodyEl.innerHTML = html;
  }

  function updateShippingBar(totalPrice) {
    if (!shipFillEl || !shipTextEl) return;
    var pct = Math.min(100, Math.round((totalPrice / FREE_SHIP_THRESHOLD) * 100));
    shipFillEl.style.width = pct + '%';

    if (totalPrice >= FREE_SHIP_THRESHOLD) {
      shipTextEl.innerHTML = '🎉 <strong>¡Envío gratis desbloqueado!</strong>';
    } else {
      var remaining = formatMoney(FREE_SHIP_THRESHOLD - totalPrice);
      shipTextEl.innerHTML = 'Faltan <strong>' + remaining + '</strong> para envío gratis';
    }
  }

  /* ═══════════════════════════════════════════
     FETCH CART
  ════════════════════════════════════════════ */
  function refresh() {
    if (isFetching) return;
    isFetching = true;
    renderSkeleton();

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderCart(cart); })
      .catch(function () {
        if (bodyEl) bodyEl.innerHTML = '<p style="color:rgba(255,255,255,.5);text-align:center;padding:40px 20px;font-family:Inter,sans-serif;font-size:13px;">Error al cargar el carrito.<br>Por favor recarga la página.</p>';
      })
      .finally(function () { isFetching = false; });
  }

  /* ═══════════════════════════════════════════
     CART MUTATIONS
  ════════════════════════════════════════════ */
  function changeItem(key, qty) {
    /* Find the item row and add loading class */
    var row = bodyEl ? bodyEl.querySelector('[data-key="' + key + '"]') : null;
    if (row) row.classList.add('vcd__item--loading');

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) { renderCart(cart); })
    .catch(function () {
      if (row) row.classList.remove('vcd__item--loading');
    });
  }

  /* ═══════════════════════════════════════════
     EVENT DELEGATION (inside drawer body)
  ════════════════════════════════════════════ */
  document.addEventListener('click', function (e) {

    /* ── Open triggers ── */
    var openTrigger = e.target.closest('[data-open-cart]');
    if (openTrigger) {
      e.preventDefault();
      window._vcdLastTrigger = openTrigger;
      open();
      return;
    }

    /* ── Close triggers ── */
    var closeTrigger = e.target.closest('.vcd__close, #vcd-overlay');
    if (closeTrigger) {
      close();
      return;
    }

    /* ── Stepper ── */
    var stepBtn = e.target.closest('.vcd__stepper-btn');
    if (stepBtn && !stepBtn.disabled) {
      var key = stepBtn.getAttribute('data-key');
      var qty = parseInt(stepBtn.getAttribute('data-qty'), 10);
      changeItem(key, qty);
      return;
    }

    /* ── Remove ── */
    var removeBtn = e.target.closest('.vcd__remove');
    if (removeBtn) {
      var rKey = removeBtn.getAttribute('data-key');
      changeItem(rKey, 0);
      return;
    }
  });

  /* Escape key */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen) close();
  });

  /* ═══════════════════════════════════════════
     PUBLIC API
  ════════════════════════════════════════════ */
  window.VitalCart = {
    open:    open,
    close:   close,
    refresh: refresh
  };

  /* Hook into VitalAddToCart success → auto-refresh drawer if open */
  var _origShow = null;
  if (window.VitalToast) {
    _origShow = window.VitalToast.show;
    window.VitalToast.show = function (opts) {
      if (isOpen) refresh();
      return _origShow.call(window.VitalToast, opts);
    };
  } else {
    /* VitalToast not yet loaded — patch after DOMContentLoaded */
    document.addEventListener('DOMContentLoaded', function () {
      if (window.VitalToast && !_origShow) {
        _origShow = window.VitalToast.show;
        window.VitalToast.show = function (opts) {
          if (isOpen) refresh();
          return _origShow.call(window.VitalToast, opts);
        };
      }
    });
  }

})();
